import { GridOptions, GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { Component, ElementRef, TemplateRef, ViewChild } from "@angular/core";
import { Product, JoDetail, PoDetail } from "app/model/entities/entity-model";
import { AgFns, GridOptionsExt } from "app/shared/ag-fns";

import { DialogBaseComponent, DialogParams } from "app/shared/dialog-base.component";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { DomainService } from "app/shared/domain.service";
import { PoUtilsService } from "app/shared/po-utils.service";

export interface PurchaseOrderApplyChangeDialogParams extends DialogParams {
  poDetail: PoDetail;
  pohRemainingQty: number;
  pohIsHeld: boolean;
}

@Component({
  templateUrl: "./purchase-order-apply-change-dialog.component.html",
  standalone: false,
})
export class PurchaseOrderApplyChangeDialogComponent extends DialogBaseComponent<PurchaseOrderApplyChangeDialogParams> {
  @ViewChild("altStyleCell") altStyleCell: TemplateRef<any>;
  @ViewChild("qtyInput") qtyInput: ElementRef<HTMLInputElement>;

  poDetail: PoDetail;
  poDetails: PoDetail[];
  podGridOptions: GridOptionsExt;

  joDetails: JoDetail[] = [];
  jodGridOptions: GridOptionsExt;
  joDetailId: number;

  issGridOptions: GridOptionsExt;

  pohRemainingQty: number;
  pohIsHeld: boolean;

  isAccountOwned: boolean;
  applyTo: string;
  applyQty: number;
  applyJod: JoDetail;
  applyIss: Product;

  private readonly COMPACT_HEIGHT = "420px";
  private readonly EXPANDED_HEIGHT = "700px";

  constructor(
    public config: DynamicDialogConfig<PurchaseOrderApplyChangeDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
    private poUtilsService: PoUtilsService,
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, {
      width: "1200px",
      height: this.COMPACT_HEIGHT,
    });
    this.prepare();
  }

  async prepare() {
    this.poDetail = this.params.poDetail;
    this.pohRemainingQty = this.params.pohRemainingQty;
    this.pohIsHeld = this.params.pohIsHeld;
    this.isAccountOwned = this.poDetail.joDetail?.joHeader?.isAccountOwnedOrder ?? false;

    this.config.header = `Modify Purchase Order Detail: ${this.poDetail.id}`;

    this.poDetails = [this.poDetail];
    this.applyTo = "default";
    this.podGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPodGridReady,
    });

    this.jodGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onJodGridReady,
        onRowSelected: this.onJodRowSelected,
      },
      { detailProperty: "poDetails" },
    );
    this.updateJodMasterDetail(this.jodGridOptions);

    this.issGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onIssGridReady,
      onRowSelected: this.onIssRowSelected,
      rowModelType: "serverSide",
    });

    this.isPageReady = true;

    // Focus the quantity input after view renders
    setTimeout(() => this.focusQtyInput());
  }

  private focusQtyInput() {
    this.qtyInput?.nativeElement?.focus();
    this.qtyInput?.nativeElement?.select();
  }

  onPodGridReady(event: GridReadyEvent) {
    const colDefs = [
      { headerName: "Account", field: "joDetail.joHeader.account.accountName", ...AgFns.flexWidth(120) },
      { headerName: "Description", field: "product.productType.description", ...AgFns.flexWidth(200) },
      { headerName: "SKU", field: "product.productType.style", ...AgFns.flexWidth(100) },
      { headerName: "Features", field: "product.featureChoicesExtract", ...AgFns.flexWidth(140) },
      {
        headerName: "Order",
        field: "orderQty",
        ...AgFns.fixWidth(140),
        type: "numericColumn",
      },
      {
        headerName: "Held",
        field: "heldQty",
        ...AgFns.fixWidth(140),
        type: "numericColumn",
      },
      {
        headerName: "Needed",
        type: "numericColumn",
        ...AgFns.fixWidth(140),
        valueGetter: (params) => {
          const pod = <PoDetail>params.data;
          return pod.getRemainingQty();
        },
      },
    ];
    AgFns.initGrid(event, colDefs);
    AgFns.sizeColumnsToFit(this.podGridOptions);
  }

  onJodGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "Account",
        field: "joHeader.account.accountName",
        cellRenderer: "agGroupCellRenderer",
      },
      // { headerName: 'J/O Detail Id', field: 'id', filter: 'agTextColumnFilter' },
      { headerName: "Job Order Status", field: "joHeader.joStatus.name" },
      // { headerName: 'Manufacturer', field: 'product.productType.manufacturer.name', filter: 'agTextColumnFilter' },
      // { headerName: 'Description', field: 'product.productType.description', filter: 'agTextColumnFilter' },
      {
        headerName: "SKU",
        field: "product.productType.style",
        filter: "agTextColumnFilter",
      }, // may be an alt style
      {
        headerName: "Features",
        field: "product.featureChoicesExtract",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Remaining Qty",
        colId: "remainQty",
        valueGetter: (params) => {
          const jod = <JoDetail>params.data;
          return jod.getPoDetailsRemainingQty();
        },
      },
      { headerName: "Order Qty", field: "orderQty", hide: true },
      {
        headerName: "Applied Qty",
        colId: "appliedQty",
        hide: true,
        valueGetter: (params) => {
          const jod = <JoDetail>params.data;
          return jod.getItemDetailsRcvdQty();
        },
      },
      { headerName: "Cancel Qty", field: "cancelQty", hide: true },
      { headerName: "Return Qty", field: "returnQty", hide: true },
      {
        ...AgFns.createButtonProps("", this.showAltStyle.bind(this), {
          label: "Show",
          canDisplay: this.hasAltStyle.bind(this),
        }),
      },
      // { ...AgFns.createCellButtonProps('Alt Style', this.altStyleCell), },
      // <button *ngIf="hasAltStyle(row)" (click)="showAltStyle(row)">Show</button>
    ];
    const sortFields = ["product.choiceValue"];
    const sortModel = sortFields.map((sf) => {
      return { colId: sf, sort: "asc" as const };
    });

    AgFns.initGrid(event, colDefs, sortModel);
  }

  updateJodMasterDetail(parentGridOptions: GridOptions) {
    const detailGridOptions = AgFns.createDetailGridOptions();
    detailGridOptions.columnDefs = [
      { headerName: "Po Detail Id", field: "id" },
      { headerName: "Po Header Id", field: "poHeaderId" },
      { headerName: "Po Status", field: "poHeader.poStatus.name" },
      { headerName: "Order Qty", field: "orderQty" },
    ];
    AgFns.updateColDefs(detailGridOptions.columnDefs);
    parentGridOptions.detailCellRendererParams = {
      refreshStrategy: "everything",
      detailGridOptions: detailGridOptions,
      getDetailRowData: (params) => {
        const joDetail = params.data as JoDetail;
        params.successCallback(joDetail.poDetails);
      },
    };
  }

  onIssGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "Manufacturer",
        field: "productType.manufacturer.name",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Description",
        field: "productType.description",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "SKU",
        field: "productType.style",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Features",
        field: "featureChoicesExtract",
        filter: "agTextColumnFilter",
      },
    ];
    const sortFields = ["productType.manufacturer.name", "productType.description", "productType.style", "choiceValue"];
    const sortModel = sortFields.map((sf) => {
      return { colId: sf, sort: "asc" as const };
    });
    AgFns.initGrid(event, colDefs, sortModel);

    const gridApi = this.issGridOptions.apiX;

    // const filter1 = gridApi.getFilterInstance('productType.manufacturer.name');
    // filter1.setModel({
    //   filterType: 'text',
    //   type: 'equals',
    //   filter: this.poDetail.product.productType.manufacturer.name
    // });
    gridApi.setColumnFilterModel("productType.manufacturer.name", {
      filterType: "text",
      type: "equals",
      filter: this.poDetail.product.productType.manufacturer.name,
    });

    // const filter2 = gridApi.getFilterInstance('productType.style');
    // filter2.setModel({
    //   filterType: 'text',
    //   type: 'equals',
    //   filter: this.poDetail.product.productType.style
    // });
    gridApi.setColumnFilterModel("productType.style", {
      filterType: "text",
      type: "equals",
      filter: this.poDetail.product.productType.style,
    });

    gridApi.onFilterChanged();

    const ds = AgFns.buildDatasource(() => this.dbQueryService.createProductQuery());
    gridApi.setGridOption("serverSideDatasource", ds);
  }

  onIssRowSelected(e: RowSelectedEvent) {
    if (!e.node.isSelected()) {
      return;
    }
    this.applyIss = e.data as Product;
  }

  onJodRowSelected(e: RowSelectedEvent) {
    if (!e.node.isSelected()) {
      return;
    }
    this.applyJod = e.data as JoDetail;
  }

  applyToChanged(value: string) {
    // Resize dialog based on whether a grid needs to be shown
    const needsGrid = value === "jod" || (value === "cancel" && this.poDetail.joDetailId != null);
    this.config.height = needsGrid ? this.EXPANDED_HEIGHT : this.COMPACT_HEIGHT;

    if (value === "jod") {
      this.findMatching();
    }

    // Return focus to quantity input
    setTimeout(() => this.focusQtyInput());
  }

  async applyQtyChanged(value: string) {
    if (value === "" || value == null) {
      return;
    }
    const val = Number.parseInt(value, 10);
    await this.checkApplyQty(val);
  }

  async checkApplyQty(val: number) {
    const remainingQty = this.poDetail.getRemainingQty();
    const maxQty = this.applyTo != "unhold" ? remainingQty : this.poDetail.heldQty;
    if (val <= 0 || val > maxQty) {
      await this.dialogService.showOkMessage("Invalid Quantity", `Quantity must be between than 1 and ${maxQty} inclusive.`);
      return false;
    } else {
      return true;
    }
  }

  async findMatching() {
    this.joDetailId = null;
    this.applyJod = null;
    await AgFns.waitForGrid(this.jodGridOptions);
    await AgFns.busyGrid(this.jodGridOptions, this, async () => {
      // Finds all JoDetails that match on style or altStyle
      const joDetails = await this.dbQueryService.getJoDetailsForPoModification(this.poDetail.product.productTypeId);
      // problem here is that we really only want to steal from one other pod not multiple ones.
      this.joDetails = joDetails.filter((jod) => jod.getPoDetailsRemainingQty() > 0 && jod.id !== this.poDetail.joDetailId);
      AgFns.autoSizeAllColumns(this.jodGridOptions);
    });
    if (this.joDetails.length === 0) {
      await this.dialogService.showOkMessage("None found", "No matching unfulfilled JoDetails found.");
    }
  }

  hasAltStyle(jod: JoDetail) {
    return jod.product.productTypeId !== this.poDetail.product.productTypeId;
  }

  async showAltStyle(jod: JoDetail) {
    const its = jod.product.productType;
    await this.dialogService.showOkMessage(
      "Alt Style",
      `
      Manufacturer: ${its.manufacturer.name}<br>
      Description: ${its.description}<br>
      Style: ${its.style}
      `,
    );
  }

  async apply() {
    let ok: boolean;
    ok = await this.checkApplyQty(this.applyQty);
    if (!ok) return false;

    if (this.applyTo === "jod") {
      ok = await this.applyToJod();
    } else if (this.applyTo === "stock") {
      ok = await this.applyToStock();
    } else if (this.applyTo === "cancel") {
      ok = await this.applyToCancel();
    } else if (this.applyTo === "hold") {
      ok = await this.applyToHold(1);
    } else if (this.applyTo === "unhold") {
      ok = await this.applyToHold(-1);
    } else {
      ok = await this.applyToDefault();
    }
    if (ok) {
      this.dialogRef.close(true);
    }
  }

  private async confirmContinue() {
    if (this.pohRemainingQty > this.applyQty || this.pohIsHeld) {
      const ok = await this.dialogService.yesNoDialog("Confirm", "Are you sure?");
      return ok;
    } else {
      const ok = await this.dialogService.yesNoDialog(
        "Confirm",
        "This operation will mark this PO complete.  You will not be able to undo any part of this PO after this is completed. Do you want to continue?",
      );
      return ok;
    }
  }

  async applyToJod() {
    if (this.applyJod == null) {
      await this.dialogService.showOkMessage("No JoDetail selected", "You must select a JoDetail record");
      return false;
    }

    if (this.applyJod.getPoDetailsRemainingQty() < this.applyQty) {
      await this.dialogService.showOkMessage(
        "Too many for this Job Order",
        `This Job Order Detail only requires ${this.applyJod.getPoDetailsRemainingQty()} items`,
      );
      return false;
    }
    if (!(await this.confirmContinue())) {
      return false;
    }
    return await this.poUtilsService.applyPodToJod(this.poDetail, this.applyJod, this.applyQty);
  }

  async applyToStock() {
    if (!(await this.confirmContinue())) {
      return false;
    }
    return await this.poUtilsService.applyPodToStock(this.poDetail, this.applyQty);
  }

  async applyToDefault() {
    if (!(await this.confirmContinue())) {
      return false;
    }
    return await this.poUtilsService.applyPodToDefault(this.poDetail, this.applyQty);
  }

  async applyToHold(sign: 1 | -1) {
    if (!(await this.confirmContinue())) {
      return false;
    }
    return await this.poUtilsService.applyPodToHold(this.poDetail, sign * this.applyQty);
  }

  async applyToCancel() {
    if (this.poDetail.joDetailId != null) {
      if (this.applyIss == null) {
        await this.dialogService.showOkMessage("No Replacement Item Style/Size selected", "You must select a Style and Size.");
        return false;
      }
    } else {
      this.applyIss = null;
    }
    if (!(await this.confirmContinue())) {
      return false;
    }
    return await this.poUtilsService.applyPodToCancel(this.poDetail, this.applyIss, this.applyQty);
  }

  cancel() {
    this.dialogRef.close(null);
  }
}
